import React, { useEffect, useContext, useState, useMemo } from 'react'
import { CompanyContext } from 'contexts/CompanyContext'
import FormDesription from './FormDesription'
import { Trans } from 'react-i18next'
import { AIContext, aiSizeOptions } from 'contexts/AIContext'
import { PLACEMENT } from 'components/ui/utils/constant'
import AIVideoDropdownModels from 'views/ai_video/components/AIVideoDropdownModels'
import AIVideoDropdownSizes from 'views/ai_video/components/AIVideoDropdownSizes'
import ModelCard from './ModelCard'
import AspectRatioCard from './AspectRatioCard'
import { Card } from 'components/ui'
import { 
    HiLightningBolt, 
    HiPlay,
    HiCollection,
    HiInformationCircle,
    HiEye
} from 'react-icons/hi'
import { BsStars } from 'react-icons/bs'
import { RiVideoOnAiLine } from 'react-icons/ri'

const AIVideos = ({ isModal = false }) => {

    const { getDriverModel, aiModels, aiVideoModels } = useContext(AIContext);
    const { company, updateCompany } = useContext(CompanyContext);
    const { model: videoModel, size: videoSize } = getDriverModel("video");


    // Get available models and current model info
    const availableModels = useMemo(() => 
        aiVideoModels?.filter(m => !m.disabled) || [], 
        [aiVideoModels]
    );
    
    const currentModel = useMemo(() => 
        availableModels.find(m => m.model === videoModel) || availableModels[0],
        [availableModels, videoModel]
    );

    // Get available size options for the current model
    const availableSizeOptions = useMemo(() => {
        if (!currentModel?.availableSizes) return [];
        
        return aiSizeOptions.filter(sizeOption => 
            currentModel.availableSizes.includes(sizeOption.id)
        );
    }, [currentModel, aiSizeOptions]);

    const updateCompanyVideoModel = async (_model) => {
        const ai_preferences = {
            ...company?.ai_preferences,
            video_model: _model
        }
        await updateCompany({ ai_preferences });
    }

    const updateCompanyVideoSize = async (_size) => {
        const ai_preferences = {
            ...company?.ai_preferences,
            video_aspect_ratio: _size
        }
        await updateCompany({ ai_preferences });
    }

    useEffect(() => {
        if (!!videoModel && !aiModels?.find(m => m.model == videoModel)?.availableSizes?.includes(videoSize)) {
            updateCompanyVideoSize(aiSizeOptions?.filter(e => aiModels?.find(m => m.model == videoModel)?.availableSizes?.includes(e.id))?.[0]?.id);
        }
    }, [videoModel]);

    return (
        <div className="w-full space-y-8">
            {/* Header Section */}
            <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg">
                        <HiPlay className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        AI Video Generation Settings
                    </h2>
                </div>
                <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                    Configure your AI video generation preferences. Choose from cutting-edge models and customize aspect ratios to create stunning videos for your content.
                </p>
            </div>

            {/* Current Configuration Summary */}
            <Card className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-purple-200 dark:border-purple-700/50">
                <div className="p-0">
                    <div className="flex items-center gap-3 mb-4">
                        <BsStars className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Current Configuration
                        </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-center gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                            <img 
                                src={currentModel?.image} 
                                alt={currentModel?.name}
                                className="w-8 h-8 rounded"
                            />
                            <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                    {currentModel?.name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {currentModel?.price} credits per video
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                                <HiCollection className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                            </div>
                            <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                    {videoSize} Aspect Ratio
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {videoSize === '16:9' && 'Landscape format'}
                                    {videoSize === '1:1' && 'Square format'}
                                    {videoSize === '9:16' && 'Portrait format'}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded flex items-center justify-center">
                                <HiLightningBolt className="w-4 h-4 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                    {currentModel?.acceptsImageInput ? 'Image-to-Video' : 'Text-to-Video'}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Generation mode
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* AI Video Model Selection */}
            <div className="space-y-6">
                {/* Quick Selector Dropdowns for Compact View */}
                <div className="lg:hidden space-y-4">
                    <AIVideoDropdownModels 
                        model={videoModel}
                        setModel={updateCompanyVideoModel}
                        className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                        parentClassName="w-full"
                        showCreditsInToggle={true}
                        truncate={false}
                    />
                </div>

                {/* Model Cards for Desktop */}
                <div className="hidden lg:block space-y-6">
                    {availableModels.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <RiVideoOnAiLine className="w-5 h-5 text-yellow-500" />
                                <h4 className="font-medium text-gray-900 dark:text-white">Video Models</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {availableModels.map(model => (
                                    <ModelCard
                                        key={model.model}
                                        model={model}
                                        isSelected={model.model === videoModel}
                                        onSelect={updateCompanyVideoModel}
                                        tier="premium"
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Aspect Ratio Selection */}
            <div className="space-y-6">
                <FormDesription
                    title={
                        <span className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <HiEye className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                            Video Aspect Ratio
                        </span>
                    }
                    desc={
                        <span className="text-gray-600 dark:text-gray-300">
                            Choose the aspect ratio for your generated videos. Different ratios work better for different social media platforms.
                        </span>
                    }
                />

                {/* Quick Selector for Mobile */}
                <div className="lg:hidden">
                    <AIVideoDropdownSizes 
                        model={videoModel}
                        size={videoSize}
                        setSize={updateCompanyVideoSize}
                        className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                        parentClassName="w-full"
                    />
                </div>

                {/* Aspect Ratio Cards for Desktop */}
                <div className="hidden lg:block">
                    <div className="grid gap-4 max-w-4xl" style={{ gridTemplateColumns: `repeat(${Math.min(availableSizeOptions.length, 6)}, minmax(0, 1fr))` }}>
                        {availableSizeOptions.map((sizeOption) => (
                            <AspectRatioCard
                                key={sizeOption.id}
                                sizeOption={sizeOption}
                                isSelected={videoSize === sizeOption.id}
                                onSelect={updateCompanyVideoSize}
                            />
                        ))}
                    </div>
                </div>
            </div>



            {/* Footer Note */}
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center justify-center gap-2 mb-1">
                    <HiInformationCircle className="w-4 h-4" />
                    <span className="font-medium">Settings Auto-Save</span>
                </div>
                <p>Your AI video preferences are automatically saved and will be applied to all future video generations.</p>
            </div>
        </div>
    )
}

export default AIVideos
